import React from "react";
import { Lock, Check, Gift } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";

const PLAN_LABEL = { standard: "Standard Clinic", enterprise: "Enterprise Hospital" };
const PLAN_PRICE = { standard: 150000, enterprise: 450000 };

// Blurs the app behind a subscription upgrade wall — ONLY when the developer has
// activated the paywall for this organisation from the superadmin portal.
export function PaywallGate({ children }) {
  const { paywall, org } = useAuth();
  const navigate = useNavigate();
  const activePlan = org?.plan;
  const required = paywall?.plan_required || "standard";
  const satisfied = ["standard", "enterprise"].includes(activePlan);
  const inExtendedTrial = paywall?.trial_ends_at && new Date(paywall.trial_ends_at) > new Date();
  const locked = paywall?.enabled && !satisfied && !inExtendedTrial;

  if (!locked) return children;

  return (
    <div className="relative min-h-[70vh]">
      <div className="pointer-events-none select-none opacity-40 blur-sm">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/40 p-4 backdrop-blur-md" data-testid="paywall-overlay">
        <div className="w-full max-w-md rounded-2xl border border-teal-200 bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 ring-8 ring-teal-50/50">
            <Lock className="h-7 w-7" />
          </div>
          <h2 className="mt-5 font-display text-2xl font-bold text-slate-900">Subscription required</h2>
          <p className="mt-2 text-sm text-slate-500">
            Your FHAPAS trial access has ended. Upgrade to the <b>{PLAN_LABEL[required] || required}</b> plan to
            continue using {org?.name || "your organisation"}.
          </p>
          <div className="mt-5 rounded-xl bg-teal-50 p-4 text-left">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-slate-900">{PLAN_LABEL[required] || required}</span>
              <span className="font-display text-xl font-extrabold text-teal-700">
                {money(PLAN_PRICE[required] || 0, org?.currency || "NGN")}<span className="text-xs font-medium text-slate-500">/mo</span>
              </span>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              {["All clinical & revenue modules", "Priority support", "Unlimited patients"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {f}</li>
              ))}
            </ul>
          </div>
          <Button
            data-testid="paywall-upgrade-button"
            className="mt-5 w-full bg-teal-700 hover:bg-teal-800"
            onClick={() => navigate("/app/subscription")}
          >
            View plans & upgrade
          </Button>
        </div>
      </div>
    </div>
  );
}

// Slim notice showing remaining days on an event-code extended trial.
export function TrialBanner() {
  const { paywall, org } = useAuth();
  const navigate = useNavigate();
  if (!paywall?.trial_ends_at || ["standard", "enterprise"].includes(org?.plan)) return null;
  const days = Math.ceil((new Date(paywall.trial_ends_at) - new Date()) / 86400000);
  if (days <= 0) return null;
  return (
    <div data-testid="trial-banner" className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
      <span className="flex items-center gap-2"><Gift className="h-4 w-4 text-amber-600" /> <b>{paywall.trial_promo}</b> extended free trial · <b data-testid="trial-days-left">{days}</b> day{days === 1 ? "" : "s"} left</span>
      <button type="button" onClick={() => navigate("/app/subscription")} className="font-semibold text-amber-800 hover:underline" data-testid="trial-banner-plans">View plans</button>
    </div>
  );
}